import React from "react";
import { Controller, useForm } from "react-hook-form";
import { Box, Button } from "@mui/material";
import { useAuth, useDOUploadFile } from "hooks";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import BaseSelect from "components/Shared/Base/BaseSelect";
import FileDropzone from "components/Shared/FileDropzone";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";

const INITIAL_VALUES = {
    title: "",
    vendorDocumentType: "",
    attachments: [],
};

const VendorProfileDocumentForm = ({
    isBusy = false,
    onSubmit = () => { },
    onCancel = () => { },
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
    } = useForm({ defaultValues: INITIAL_VALUES });

    const { viewEntity } = useAuth();
    const { handleUploadFiles, uploadingFiles } = useDOUploadFile();

    const { data: vendorDocumentType = [], isLoading: loadingVendorDocumentType } =
        useListListQuery({
            type: ENTITY_LIST_CONSTANTS.VENDOR_DOCUMENT_TYPES,
            status: "Active",
        });

    const _mappedVendorDocumentType = React.useMemo(
        () =>
            Array.isArray(vendorDocumentType)
                ? vendorDocumentType.map((item) => ({
                    label: item?.name,
                    value: item?._id,
                }))
                : [],
        [vendorDocumentType]
    );

    const handleFormSubmit = async (data) => {
        let attachments = data.attachments || [];
        if (attachments.length > 0) {
            try {
                const { uploadedFiles } = await handleUploadFiles(attachments, {
                    folderName: "Document",
                    entity: viewEntity?.entity?._id,
                });
                attachments = uploadedFiles;
            } catch (error) {
                return;
            }
        }
        onSubmit({
            title: data.title,
            vendorDocumentType: data.vendorDocumentType || null,
            attachments,
        });
        reset(INITIAL_VALUES);
    };

    return (
        <LoadingWrapper loading={isBusy || uploadingFiles}>
            <Box display="flex" flexDirection="column" gap={2}>
                <Controller
                    name="title"
                    control={control}
                    rules={{ required: "Title is required" }}
                    render={({ field }) => (
                        <BaseTextField
                            fullWidth
                            label="Title"
                            type="text"
                            {...field}
                            onChange={(e) => setValue("title", e.target.value)}
                            error={!!errors.title}
                            helperText={errors.title?.message}
                        />
                    )}
                />
                <Controller
                    name="vendorDocumentType"
                    control={control}
                    render={({ field }) => (
                        <BaseSelect
                            {...field}
                            fullWidth
                            label="Document Type"
                            options={_mappedVendorDocumentType}
                            getOptionLabel={(opt) => opt.label || ""}
                            getOptionValue={(opt) => opt.value || ""}
                            loading={loadingVendorDocumentType}
                            loadingText="Loading Document Types..."
                        />
                    )}
                />
                <Controller
                    name="attachments"
                    control={control}
                    render={({ field }) => (
                        <FileDropzone
                            files={field.value || []}
                            onFileChange={(files) => setValue("attachments", files)}
                            multiple={true}
                            maxFiles={5}
                        />
                    )}
                />
                <Box display="flex" justifyContent="flex-end" gap={1.5} mt={1}>
                    <Button disabled={isBusy} onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant="outlined" disabled={isBusy} onClick={handleSubmit(handleFormSubmit)}>
                        Add Document
                    </Button>
                </Box>
            </Box>
        </LoadingWrapper>
    );
};

export default VendorProfileDocumentForm;